import { useState } from "react";
import {
  Loader2, Sparkles, ImageIcon, Wand2, Check, X, Download, Video, ExternalLink, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { studioApi } from "../studio/api";
import { plannerApi } from "./api";
import {
  ART_FORMATS, ART_TEMPLATES, VIDEO_FORMATS, aspectOf, renderArtPiece, downloadDataUrl,
} from "./helpers";
import { SIGNAL_COLOR_OPTIONS, CTA_COLOR_OPTIONS } from "../studio/canvas";

const inputCls =
  "w-full rounded-lg bg-[#111111] border border-white/10 px-3 py-2 text-sm text-white placeholder:text-zinc-600 outline-none focus:border-[#FF8A00]/60 transition";
const labelCls = "block text-[10px] uppercase tracking-wider text-zinc-500 mb-1 font-semibold";
const btnCls =
  "inline-flex items-center gap-1.5 rounded-lg border border-white/10 px-3 py-2 text-xs font-semibold text-zinc-200 hover:text-white hover:border-white/25 disabled:opacity-40 transition";

export const PieceEditor = ({ piece, assets, logoImg, bg, onSetBg, render, onSetRender, onPatch, onOpenVideo, onOpenStudio }) => {
  const [genBg, setGenBg] = useState(false);
  const [rendering, setRendering] = useState(false);
  const [regen, setRegen] = useState(false);
  const isVideo = piece.type === "video";
  const assignedAsset = assets.find((a) => a.id === piece.assigned_asset_id);
  const activeBg = bg || assignedAsset?.dataUrl || null;

  const set = (k) => (e) => onPatch({ [k]: e.target.value });

  const onGenerateBg = async () => {
    const prompt = (piece.background_prompt || piece.headline || "").trim();
    if (!prompt) return toast.error("Escribe un prompt de fondo");
    setGenBg(true);
    try {
      const d = await studioApi.generateImage({
        prompt,
        model: "gemini",
        aspect: isVideo ? "story" : aspectOf(piece.format),
      });
      onSetBg(`data:${d.mime_type || "image/png"};base64,${d.image_base64}`);
      toast.success(`Fondo generado · ${d.model}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo generar el fondo");
    } finally {
      setGenBg(false);
    }
  };

  const onRenderArt = async () => {
    setRendering(true);
    try {
      const dataUrl = await renderArtPiece(piece, { backgroundDataUrl: activeBg, logo: logoImg });
      onSetRender(dataUrl);
      onPatch({ status: piece.status === "pending" ? "pending" : piece.status });
      toast.success("Arte generado");
    } catch (err) {
      toast.error("No se pudo generar el arte");
    } finally {
      setRendering(false);
    }
  };

  const onRegenCopy = async () => {
    setRegen(true);
    try {
      const d = await plannerApi.regenerateCopy({
        template: isVideo ? "tip" : piece.template,
        context: piece.notes || piece.headline || "Post Daos Ads",
        current_headline: piece.headline,
      });
      onPatch({ headline: d.headline, subtitle: d.subtitle, cta: d.cta });
      toast.success("Copy regenerado con Claude");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo regenerar el copy");
    } finally {
      setRegen(false);
    }
  };

  return (
    <div data-testid={`planner-editor-${piece.id}`} className="rounded-xl border border-white/10 bg-[#0A0A0A] p-4 grid lg:grid-cols-[1fr_260px] gap-5">
      <div className="space-y-3">
        <div className="grid sm:grid-cols-3 gap-3">
          <div>
            <label className={labelCls}>Tipo</label>
            <select data-testid={`piece-type-${piece.id}`} value={piece.type} onChange={(e) => onPatch({
              type: e.target.value,
              format: e.target.value === "video" ? "reels" : "ig-square",
            })} className={inputCls}>
              <option value="art">Arte estática</option>
              <option value="video">Video</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Formato</label>
            <select data-testid={`piece-format-${piece.id}`} value={piece.format} onChange={set("format")} className={inputCls}>
              {(isVideo ? VIDEO_FORMATS : ART_FORMATS).map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>{isVideo ? "Duración (s)" : "Plantilla"}</label>
            {isVideo ? (
              <select data-testid={`piece-duration-${piece.id}`} value={piece.duration || 15}
                onChange={(e) => onPatch({ duration: Number(e.target.value) })} className={inputCls}>
                {[5, 10, 15, 30, 60].map((d) => <option key={d} value={d}>{d}s</option>)}
              </select>
            ) : (
              <select data-testid={`piece-template-${piece.id}`} value={piece.template} onChange={set("template")} className={inputCls}>
                {ART_TEMPLATES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            )}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Bajada (eyebrow)</label>
            <input data-testid={`piece-eyebrow-${piece.id}`} value={piece.eyebrow || ""} onChange={set("eyebrow")} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>CTA</label>
            <input data-testid={`piece-cta-${piece.id}`} value={piece.cta || ""} onChange={set("cta")} className={inputCls} />
          </div>
        </div>

        <div>
          <label className={labelCls}>Titular</label>
          <textarea data-testid={`piece-headline-${piece.id}`} rows={2} value={piece.headline || ""} onChange={set("headline")} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Subtítulo</label>
          <textarea data-testid={`piece-subtitle-${piece.id}`} rows={2} value={piece.subtitle || ""} onChange={set("subtitle")} className={inputCls} />
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Palabras en degradado</label>
            <input data-testid={`piece-gradient-${piece.id}`} value={piece.gradient_words || ""} onChange={set("gradient_words")}
              placeholder="separadas por coma" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Palabras señal</label>
            <input data-testid={`piece-signal-${piece.id}`} value={piece.signal_words || ""} onChange={set("signal_words")}
              placeholder="separadas por coma" className={inputCls} />
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Color señal</label>
            <select data-testid={`piece-signal-color-${piece.id}`} value={piece.signal_color || "amber"} onChange={set("signal_color")} className={inputCls}>
              {SIGNAL_COLOR_OPTIONS.filter((o) => o.value).map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Color CTA</label>
            <select data-testid={`piece-cta-color-${piece.id}`} value={piece.cta_color || "gradient"} onChange={set("cta_color")} className={inputCls}>
              {CTA_COLOR_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Fondo</label>
            <select data-testid={`piece-bg-source-${piece.id}`} value={piece.background_source || "ai"} onChange={set("background_source")} className={inputCls}>
              <option value="ai">Generar con IA</option>
              <option value="upload">Asset de la biblioteca</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Asset asignado</label>
            <select data-testid={`piece-asset-${piece.id}`} value={piece.assigned_asset_id || ""}
              onChange={(e) => onPatch({ assigned_asset_id: e.target.value || null })} className={inputCls}>
              <option value="">— ninguno —</option>
              {assets.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className={labelCls}>Prompt visual del fondo</label>
          <textarea data-testid={`piece-bg-prompt-${piece.id}`} rows={2} value={piece.background_prompt || ""} onChange={set("background_prompt")} className={inputCls} />
        </div>

        <div className="flex flex-wrap gap-2 pt-1">
          <button type="button" data-testid={`piece-gen-bg-${piece.id}`} onClick={onGenerateBg}
            disabled={genBg || piece.background_source === "upload"}
            title={piece.background_source === "upload" ? "Esta pieza usa un asset de tu biblioteca" : "Generar fondo con IA"}
            className={btnCls}>
            {genBg ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImageIcon className="h-3.5 w-3.5" />} Generar fondo IA
          </button>
          <button type="button" data-testid={`piece-regen-copy-${piece.id}`} onClick={onRegenCopy} disabled={regen} className={btnCls}>
            {regen ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Regenerar copy
          </button>
          {!isVideo ? (
            <>
              <button type="button" data-testid={`piece-render-${piece.id}`} onClick={onRenderArt} disabled={rendering}
                className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-extrabold text-[#050505] bg-gradient-to-r from-[#FF3B1F] via-[#FF8A00] to-[#FFD23F] hover:opacity-90 disabled:opacity-50 transition">
                {rendering ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />} Generar arte
              </button>
              <button type="button" data-testid={`piece-open-studio-${piece.id}`} onClick={() => onOpenStudio(activeBg)} className={btnCls}>
                <ExternalLink className="h-3.5 w-3.5" /> Abrir en Studio
              </button>
            </>
          ) : (
            <button type="button" data-testid={`piece-open-video-${piece.id}`} onClick={() => onOpenVideo(activeBg)}
              className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-extrabold text-[#050505] bg-gradient-to-r from-[#FF3B1F] via-[#FF8A00] to-[#FFD23F] hover:opacity-90 transition">
              <Video className="h-3.5 w-3.5" /> Generar video en Studio Video
            </button>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          <button type="button" data-testid={`piece-approve-${piece.id}`} onClick={() => onPatch({ status: "approved" })}
            className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs font-semibold text-emerald-300 hover:bg-emerald-500/20 transition">
            <Check className="h-3.5 w-3.5" /> Aprobar
          </button>
          <button type="button" data-testid={`piece-reject-${piece.id}`} onClick={() => onPatch({ status: "rejected" })}
            className="inline-flex items-center gap-1.5 rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-xs font-semibold text-rose-300 hover:bg-rose-500/20 transition">
            <X className="h-3.5 w-3.5" /> Descartar
          </button>
        </div>
      </div>

      <div className="space-y-3">
        <label className={labelCls}>Vista previa</label>
        <div className="rounded-xl border border-white/10 bg-[#050505] overflow-hidden min-h-[200px] flex items-center justify-center">
          {render ? (
            <img data-testid={`piece-preview-${piece.id}`} src={render} alt="preview" className="w-full h-auto" />
          ) : activeBg ? (
            <img src={activeBg} alt="fondo" className="w-full h-auto opacity-70" />
          ) : (
            <span className="text-[11px] text-zinc-600 px-4 py-10 text-center">
              {isVideo ? "Los videos se componen en Studio Video." : "Genera el arte para ver la vista previa."}
            </span>
          )}
        </div>
        {render && (
          <button type="button" data-testid={`piece-download-${piece.id}`}
            onClick={() => downloadDataUrl(render, `daos-${piece.date}-${piece.network}.png`)}
            className={btnCls + " w-full justify-center"}>
            <Download className="h-3.5 w-3.5" /> Descargar PNG
          </button>
        )}
        {piece.notes && (
          <p className="text-[11px] text-zinc-500 leading-relaxed">
            <span className="text-zinc-400 font-semibold">Propósito: </span>{piece.notes}
          </p>
        )}
      </div>
    </div>
  );
};
